import React, { useCallback, useMemo, useState } from 'react';
import { App, Button, Tooltip } from 'antd';

import RecordBadge, { RecordIdentity } from '../common/RecordBadge';
import ShareModal from '../common/ShareModal';
import TempShareLinkSection, { QUERY_ME_UNPUBLISHABLE } from '../common/TempShareLinkSection';
import { SHARE } from '../icons';
import { createFilterShortLink } from '../../services/shortLinks';
import { buildShortLinkUrl } from '../../utils/shareUrls';
import { canonicalize, parseForBuilder, queryUsesMe } from '../../utils/kqlBuilder';
import { slugify } from '../../utils/strings';
import { describeQueryWarnings } from '../../utils/collectionLinks';
import { toShortPath } from '../../utils/recordCodes';
import { elementRefusalMessage } from '../../utils/elementRefusal';


/**
 * Shares the items view the user is looking at — filter, sort and the grid's
 * grouping period included — as a short `/s/{code}` link with a QR code.
 *
 * A filtered view becomes a `filter` short link — the compact stand-in for a
 * URL that would otherwise carry the whole query. An UNFILTERED view has no
 * query for the API to store or validate, so it becomes a `path` short link to
 * the same items page (carrying the sort and the period in the URL). Either way the visitor
 * lands on the same screen, and the link is still permission-gated at every
 * API it leads to: a short link is a convenience, never a capability.
 */
export function useItemsViewShare({
	collection,
	collectionId,
	query = '',
	sortField,
	sortDirection,
	// The grid's separator period, already resolved by the caller — omitted
	// when it is the `day` default, the way ?group= omits it from the URL.
	groupBy,
	// The intake quarantine filter (?review=), when one is on. A `filter`
	// short link has no place for it, so a review view shares as a `path`
	// link to the same URL — the teammate who opens it sees the same queue.
	review,
	// The merged list turned OFF (`?linked=0`) where it is on by default: a
	// filter short link has no place for that either, so the view shares as
	// the path it is — or the teammate lands on the merged list, different
	// rows and a different count from the "same" link.
	linkedOff = false,
	// The board on screen (KPR-1395): `{ by?, lanes? }` (by absent: the
	// list's first board element, as the page opens it). A filter short link
	// stores no view, so a board shares as the path it is.
	board = null,
	cardFiltered = false,
}) {
	const { message } = App.useApp();
	const [creating, setCreating] = useState(false);
	const [shareUrl, setShareUrl] = useState(null);

	const collectionName = collection?.name || 'Collection';

	const handleShare = useCallback(async () => {
		if (!collectionId) return;
		setCreating(true);
		try {
			// Canonical text is what every stored query carries; the server
			// canonicalizes too, so this only keeps the two in step.
			const canonical = canonicalize(query);
			const params = new URLSearchParams();
			// The path carries the query itself only when it has to stand
			// in for a filter link (a review view, the merged list turned
			// off): otherwise it is the
			// unfiltered fallback and the query rides the filter link.
			const asPath = Boolean(review) || linkedOff || Boolean(board);
			if (asPath && canonical) params.set('q', canonical);
			if (board) {
				params.set('view', 'board');
				if (board.by) params.set('board', board.by);
				if (board.lanes) params.set('lanes', board.lanes);
			}
			if (review) params.set('review', review);
			if (linkedOff) params.set('linked', '0');
			if (sortField) params.set('sort', sortField);
			if (sortDirection) params.set('dir', sortDirection);
			if (groupBy) params.set('group', groupBy);
			const search = params.toString();
			const data = await createFilterShortLink({
				collection_id: collectionId,
				query: canonical || undefined,
				sort_field: sortField,
				sort_direction: sortDirection,
				group_by: groupBy,
				review,
				asPath,
				// The collection's short form when its code is known (/c/<code>/items?…).
				path: toShortPath(`/collections/${collectionId}/items${search ? `?${search}` : ''}`),
			});
			// `url` is null when the API has no APP_BASE_URL; the code always
			// comes back, so this origin can build the link itself.
			setShareUrl(data?.url || buildShortLinkUrl(data?.code));
			// A view whose filter walks into another collection still links:
			// each teammate who opens it reads that part through their own
			// access. The API says so (`warnings`); so does this.
			const warningText = describeQueryWarnings(data?.warnings, 'short link');
			if (warningText) message.warning(warningText);
		} catch (error) {
			// An element the list's filter could mean several of: what it
			// could be (the list asks which, and then the link is made).
			message.error(elementRefusalMessage(error) || error?.response?.data?.message || 'Could not create a link for this view.');
		} finally {
			setCreating(false);
		}
	}, [collectionId, query, sortField, sortDirection, groupBy, review, linkedOff, board, message]);

	// A public link to this view can be the filter the user is looking at, or
	// the collection unfiltered — the only two things a `view` link can be, so
	// the choice is offered rather than guessed. With NO filter on screen the
	// two options are the same link, so the select is dropped and the
	// unfiltered payload is used directly.
	//
	// Known gap, deliberately not folded in: the `?card=` tile filter is not
	// part of `q`, so a card-filtered view shares as if it were unfiltered.
	// The section says so out loud rather than letting the visitor discover it.
	const canonicalQuery = useMemo(() => canonicalize(query) || '', [query]);

	// A query that does not parse canonicalizes to '' — the very same value an
	// UNFILTERED view has. Left alone that silently turns a filter nobody could
	// read into a public link to the WHOLE collection, so it is caught here and
	// the section is blocked instead of guessing which of the two was meant.
	const unreadableQuery = useMemo(
		() => Boolean((query || '').trim()) && Boolean(parseForBuilder(query).error),
		[query]
	);

	// A filter that names "me" has no reader on a public page (the API
	// refuses it: 400 query_me_unpublishable), so the Current filter scope
	// is withheld and the section says why; the whole collection still shares.
	const queryNamesMe = useMemo(() => queryUsesMe(canonicalQuery), [canonicalQuery]);
	const scopeOptions = useMemo(() => (canonicalQuery ? [
		...(queryNamesMe ? [] : [{ value: 'current', label: 'Current filter', query: canonicalQuery }]),
		{ value: 'all', label: 'Everything in this collection', query: '' },
	] : null), [canonicalQuery, queryNamesMe]);
	const caveats = [
		cardFiltered ? "The card filter on this page isn't part of the link; it shares items from every card." : null,
		queryNamesMe ? QUERY_ME_UNPUBLISHABLE : null,
		// A public link never shows an unreviewed submission (the API's
		// service scope is not-pending, whatever the link says).
		review === 'pending' || review === 'any'
			? 'Submissions waiting for review never appear on a public link.'
			: null,
	].filter(Boolean);

	const modal = (
		<ShareModal
			open={!!shareUrl}
			onClose={() => setShareUrl(null)}
			modalTitle="Share this view"
			preview={(
				<RecordIdentity
					kicker="Collection"
					badge={<RecordBadge kind="collection" record={collection} title={collectionName} />}
					// Read off the CANONICAL query, the one the payload carries,
					// so the preview and the link can never say different things.
					below={<span>{canonicalQuery ? 'Filtered item view' : 'All items'}</span>}
				/>
			)}
			shareUrl={shareUrl || ''}
			downloadFileName={`keepr-filter-${slugify(collectionName) || 'view'}`}
			className="items-page__share-modal"
			extra={(
				<TempShareLinkSection
					collectionId={collectionId}
					collection={collection}
					kind="view"
					query={canonicalQuery}
					scopeOptions={scopeOptions}
					blockedReason={unreadableQuery
						? "This filter can't be read, so it can't be turned into a public link."
						: null}
					caveat={caveats.length ? caveats.join(' ') : null}
				/>
			)}
		/>
	);

	return { share: handleShare, creating, modal };
}

export default function ItemsViewShareButton(props) {
	const { share, creating, modal } = useItemsViewShare(props);
	return (
		<>
			<Tooltip title="Share this view as a short link or QR code">
				<Button
					className="items-page__share-view"
					icon={<SHARE />}
					loading={creating}
					onClick={share}
				>
					Share view
				</Button>
			</Tooltip>
			{modal}
		</>
	);
}
